/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useState } from "react";
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle, useMediaQuery, useTheme } from "@mui/material";
import { useAppKontoContext } from "../../../../Basis/Components/AppKontoContext";
import { useAppMandantContext } from "../../../../Basis/Components/AppMandantContext";
import CheckboxInput from "../../../../Basis/Components/CheckboxInput";
import DialogBase from "../../../../Basis/Components/DialogBase";
import ErrorText from "../../../../Basis/Components/ErrorText";
import ProvisionHinweis from "../../../../Basis/Components/ProvisionHinweis";
import StripeSepaElement from "../../../../Basis/Components/StripeSepaElement";
import AuftragverarbeitungsvertragDownloadLink from "../../Components/AuftragverarbeitungsvertragDownloadLink";
import VertragZurNutzungDerSaaSLoesungDownloadLink from "../../Components/VertragZurNutzungDerSaaSLoesungDownloadLink";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nMandanten";
import useZahlungsmethodeAendern from "../../Utils/useZahlungsmethodeAendern";
import VollstaendigerName from "../../Utils/VollstaendigerName";
import FirmendatenFehlenDialog from "../FirmendatenFehlenDialog";

export interface ZahlungsmethodeAendernDialogProps {
    mandantId: string,
    mandantGeaendert: string;
    isOpen: boolean;
    onGespeichert: () => void;
    onAbgebrochen: () => void;
    hatBereitsZahlungsmethode: boolean;
}

export default function ZahlungsmethodeAendernDialog(props: ZahlungsmethodeAendernDialogProps) {
    const { t } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const [auftragsverarbeitungsvertragAkzeptiert, setAuftragsverarbeitungsvertragAkzeptiert] = useState(props.hatBereitsZahlungsmethode);
    const [nutzungsvertragAkzeptiert, setNutzungsvertragAkzeptiert] = useState(props.hatBereitsZahlungsmethode);

    const [zahlungsmethodeAendern, error] = useZahlungsmethodeAendern();

    const konto = useAppKontoContext().konto;
    const mandant = useAppMandantContext().mandant;
    const sindFirmendatenGueltig: boolean =
        !!mandant &&
        !!mandant.firmendaten.name1 &&
        !!mandant.firmendaten.strasse &&
        !!mandant.firmendaten.postleitzahl &&
        !!mandant.firmendaten.ort &&
        !!mandant.firmendaten.land &&
        !!mandant.firmendaten.region &&
        !!mandant.firmendaten.eMail;

    const [vollstaendigerName, setVollstaendigerName] = useState(VollstaendigerName.von(konto?.kontoAdressen[0].vorname, konto?.kontoAdressen[0].nachname));

    async function speichern() {
        await zahlungsmethodeAendern({
            fullName: vollstaendigerName.toString(),
            eMail: konto?.eMail ?? "",
            land: mandant?.firmendaten.land ?? "",
            mandantId: mandant?.mandantId ?? "",
            mandantGeaendert: mandant?.geaendert ?? "",
        });
        props.onGespeichert();
    }

    return (
        <>
            <DialogBase fullScreen={fullScreen} open={props.isOpen && sindFirmendatenGueltig} onClose={() => props.onAbgebrochen()} scroll="body" maxWidth="700px">
                <DialogTitle>{t(translationKeys.zahlungsmethodeAendernDialog.dialogTitle)}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(translationKeys.zahlungsmethodeAendernDialog.dialogText)}
                    </DialogContentText>

                    <ProvisionHinweis color="textSecondary" sx={{ mt: 2 }} />

                    <StripeSepaElement
                        onChange={(isComplete, fullName) => {
                            setIsComplete(isComplete);
                            setVollstaendigerName(new VollstaendigerName(fullName));
                        }}
                        fullName={vollstaendigerName.toString()}
                        required
                    />
                    <ErrorText errors={error ? [error] : []} />
                    <CheckboxInput
                        caption={<>{`${t(translationKeys.zahlungsmethodeAendernDialog.ichAkzeptiereDen)} `}<AuftragverarbeitungsvertragDownloadLink /></>}
                        feldBezeichnung=""
                        typBezeichnung=""
                        value={auftragsverarbeitungsvertragAkzeptiert}
                        onChange={setAuftragsverarbeitungsvertragAkzeptiert}
                        required
                        sx={{ mt: "1rem" }}
                    />
                    <CheckboxInput
                        caption={<>{`${t(translationKeys.zahlungsmethodeAendernDialog.ichAkzeptiereDen)} `}<VertragZurNutzungDerSaaSLoesungDownloadLink /></>}
                        feldBezeichnung=""
                        typBezeichnung=""
                        value={nutzungsvertragAkzeptiert}
                        onChange={setNutzungsvertragAkzeptiert}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => speichern()}
                        color="primary"
                        disabled={!isComplete || !vollstaendigerName.istGueltig() || !auftragsverarbeitungsvertragAkzeptiert || !nutzungsvertragAkzeptiert}
                    >
                        {t(translationKeys.zahlungsmethodeAendernDialog.speichern)}
                    </Button>
                    <Button onClick={() => props.onAbgebrochen()} color="primary">
                        {t(translationKeys.zahlungsmethodeAendernDialog.abbrechen)}
                    </Button>
                </DialogActions>
            </DialogBase>
            <FirmendatenFehlenDialog
                isOpen={props.isOpen && !sindFirmendatenGueltig}
                onOk={() => props.onAbgebrochen()}
                onAbgebrochen={() => props.onAbgebrochen()}
            />
        </>
    );
}